import { defineStore } from "pinia";
import type { JobSnapshot, SubtitleTaskItem } from "~/types/media-subtitle";
import { isTaskActive, taskItemFromJobSnapshot } from "~/utils/media-subtitle-workflow";
import { useUser, type ClientCredits } from "~/composables/useUser";

const POLL_INTERVAL_MS = 4000;
let visibilityListenerAttached = false;
const PAGE_SIZE = 30;
// A job is given up on (marked failed) only after this many "not found"
// polls in a row: one 404 can be a transient read error, and marking a still
// running translate job failed makes the user start (and pay for) another.
const MAX_NOT_FOUND_POLLS = 3;
const notFoundPolls = new Map<string, number>();

interface SubtitleTasksListResponse {
  tasks: SubtitleTaskItem[];
  nextCursor: string | null;
}

interface SubtitleTasksState {
  tasks: SubtitleTaskItem[];
  loaded: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  nextCursor: string | null;
  pollTimer: ReturnType<typeof setInterval> | null;
  isPolling: boolean;
}

/**
 * Shared source of truth for the background subtitle task list. The store is a
 * singleton, so polling survives route changes (only a hard reload resets it,
 * after which the compact list / history page re-fetch from the DB). Feeds the
 * compact list under the workflow form and the dedicated history page.
 */
// Bumped when the signed-in account changes, across store resets.
let accountEpoch = 0;

export const useSubtitleTasks = defineStore("subtitleTasks", {
  state: (): SubtitleTasksState => ({
    tasks: [],
    loaded: false,
    isLoading: false,
    isLoadingMore: false,
    nextCursor: null,
    pollTimer: null,
    isPolling: false,
  }),

  getters: {
    activeTasks: (state) => state.tasks.filter((task) => isTaskActive(task)),
    hasActiveTasks(): boolean {
      return this.activeTasks.length > 0;
    },
    hasMore: (state) => Boolean(state.nextCursor),
  },

  actions: {
    /** Drops the previous account's tasks; replies still in flight are ignored. */
    resetForAccountChange() {
      accountEpoch += 1;
      this.stopPolling();
      this.$reset();
    },

    /** Load (or reload) the first page of the user's tasks. */
    async fetchTasks() {
      const epoch = accountEpoch;
      this.isLoading = true;
      try {
        const res = await $fetch<SubtitleTasksListResponse>("/api/video-to-subtitle/platform/jobs", {
          params: { limit: PAGE_SIZE },
        });
        if (epoch !== accountEpoch) return;
        this.tasks = res.tasks;
        this.nextCursor = res.nextCursor;
        this.loaded = true;
        this.ensurePolling();
      } finally {
        this.isLoading = false;
      }
    },

    /** Append the next page (keyset pagination via `nextCursor`). */
    async loadMore() {
      if (!this.nextCursor || this.isLoadingMore) {
        return;
      }
      const epoch = accountEpoch;
      this.isLoadingMore = true;
      try {
        const res = await $fetch<SubtitleTasksListResponse>("/api/video-to-subtitle/platform/jobs", {
          params: { limit: PAGE_SIZE, before: this.nextCursor },
        });
        if (epoch !== accountEpoch) return;
        const seen = new Set(this.tasks.map((task) => task.jobId));
        this.tasks.push(...res.tasks.filter((task) => !seen.has(task.jobId)));
        this.nextCursor = res.nextCursor;
      } finally {
        this.isLoadingMore = false;
      }
    },

    /** Insert or merge a task, keeping newest first, then (re)evaluate polling. */
    upsertTask(item: SubtitleTaskItem) {
      const index = this.tasks.findIndex((task) => task.jobId === item.jobId);
      if (index >= 0) {
        this.tasks[index] = { ...this.tasks[index], ...item };
      } else {
        this.tasks.unshift(item);
      }
      this.ensurePolling();
    },

    /** Convenience wrapper used by the workflow component on every hydrate. */
    upsertFromSnapshot(job: JobSnapshot) {
      this.upsertTask(taskItemFromJobSnapshot(job));
    },

    removeTask(jobId: string) {
      this.tasks = this.tasks.filter((task) => task.jobId !== jobId);
      this.ensurePolling();
    },

    ensurePolling() {
      if (!import.meta.client) {
        return;
      }
      if (this.hasActiveTasks) {
        this.startPolling();
      } else {
        this.stopPolling();
      }
    },

    startPolling() {
      if (this.pollTimer || !import.meta.client) {
        return;
      }
      this.pollTimer = setInterval(() => {
        void this.pollActive();
      }, POLL_INTERVAL_MS);
      // Hidden tabs skip rounds (see pollActive); catch up as soon as the tab is back.
      if (!visibilityListenerAttached) {
        visibilityListenerAttached = true;
        document.addEventListener("visibilitychange", () => {
          if (!document.hidden && this.pollTimer) {
            void this.pollActive();
          }
        });
      }
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    /** Poll each active task's live status; failures are ignored until next tick. */
    async pollActive() {
      // A slow round must not overlap the next tick.
      if (this.isPolling || document.hidden) {
        return;
      }
      const active = this.activeTasks;
      if (!active.length) {
        this.stopPolling();
        return;
      }

      this.isPolling = true;
      const { applyCredits } = useUser();
      const epoch = accountEpoch;
      try {
        await Promise.all(
          active.map(async (task) => {
            try {
              const res = await $fetch<{ job: JobSnapshot; credits?: ClientCredits }>("/api/video-to-subtitle/platform/status", {
                params: { job: task.jobId },
              });
              if (epoch !== accountEpoch) return;
              // Sent once the job is final: keeps the balance current when the
              // job finishes after the user left the workflow page.
              applyCredits(res.credits);
              notFoundPolls.delete(task.jobId);
              this.upsertTask(taskItemFromJobSnapshot(res.job));
            } catch (error) {
              if (epoch !== accountEpoch) return;
              // Job gone or not ours: retrying won't help, so stop polling it.
              // Anything else (401, 429, 5xx, network) is retried next tick.
              const statusCode = (error as { statusCode?: number })?.statusCode ?? 0;
              const misses = statusCode === 404 ? (notFoundPolls.get(task.jobId) ?? 0) + 1 : 0;
              if (statusCode === 404 && misses < MAX_NOT_FOUND_POLLS) {
                notFoundPolls.set(task.jobId, misses);
                return;
              }
              if (statusCode === 400 || statusCode === 403 || statusCode === 404) {
                notFoundPolls.delete(task.jobId);
                this.upsertTask({ ...task, platformStatus: "failed", localStatus: "released" });
              }
            }
          })
        );
      } finally {
        this.isPolling = false;
      }

      this.ensurePolling();
    },
  },
});
